import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import BarChartIcon from '@mui/icons-material/BarChart';
import BiotechIcon from '@mui/icons-material/Biotech';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InsightsIcon from '@mui/icons-material/Insights';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import LinkIcon from '@mui/icons-material/Link';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DatasetIcon from '@mui/icons-material/Dataset';

import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area // Add the missing Area component
} from 'recharts';

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Add this animation-related import
import { motion } from 'framer-motion';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#4CAF50', '#FF5722', '#9C27B0'];

// Talep tahminleme POC verilerini Türkiye'ye uygun olarak güncelliyoruz
const retailDemandForecastingPOC = {
  title: "Akıllı Market: Satış Tahminleme ve Stok Yönetimi",
  description: "Geçmiş satış verilerini inceleyerek hangi ürünlerin ne kadar satılacağını önceden tahmin eden ve doğru miktarda stok bulundurmanıza yardımcı olan bir yapay zeka sistemidir.",

  // Veri seti ve analiz bilgileri - Türkiye için lokalize edildi
  dataset: {
    name: "Market Zinciri Satış Verisi",
    source: "MarketNet A.Ş. (anonim hale getirilmiştir)",
    size: "321,458 satır, 10 sütun",
    timespan: "01/01/2024 - 31/12/2024",
    countries: ["Türkiye"],
    description: "Market zinciri satış verileri - Sipariş tarihçesi, ürün, miktar, fiyat, şube bilgileri"
  },

  // Veri detaylarını Türkiye için güncelliyoruz
  dataDetails: {
    original: {
      name: "MarketNet Perakende Veri Seti",
      description: "Bu veri seti, Türkiye'nin önde gelen market zincirlerinden birinin 10 ildeki 35 şubesine ait 2024 yılı satış verilerini içermektedir. Kişisel ve ticari veriler anonim hale getirilmiştir.",
      features: [
        { name: "FisNo", description: "Fiş numarası. Her işlem için özel bir 8 haneli tam sayı." },
        { name: "UrunKodu", description: "Ürün kodu. Her farklı ürün için özel bir 6 haneli tam sayı." },
        { name: "UrunAdi", description: "Ürün adı." },
        { name: "Miktar", description: "Her işlemde satılan ürün miktarı." },
        { name: "SatisTarihi", description: "Satışın gerçekleştiği tarih ve saat." },
        { name: "BirimFiyat", description: "Birim fiyat. TL cinsinden." },
        { name: "MusteriId", description: "Müşteri numarası. Sadakat kartı kullanan müşteriler için." },
        { name: "SubeKodu", description: "Satışın gerçekleştiği şube kodu." },
        { name: "SubeSehir", description: "Şubenin bulunduğu il." },
        { name: "OdemeTipi", description: "Nakit, kredi kartı, vs." }
      ],
      statistics: {
        rows: "321,458",
        columns: "10",
        uniqueProducts: "5,230",
        uniqueCustomers: "42,156",
        stores: "35",
        timespan: "01.01.2024 - 31.12.2024"
      }
    },
    enriched: {
      description: "Orijinal veri setine ek özellikler ekleyerek analiz derinliğini artırdık",
      features: [
        { name: "ToplamTutar", description: "Sipariş tutarı (Miktar * BirimFiyat)", source: "Hesaplanan" },
        { name: "HaftaninGunu", description: "Haftanın günü (1-7)", source: "Feature Engineering" },
        { name: "Ay", description: "Ay (1-12)", source: "Feature Engineering" },
        { name: "HaftaSonu", description: "Hafta sonu mu? (0/1)", source: "Feature Engineering" },
        { name: "Mevsim", description: "Mevsim (İlkbahar, Yaz, Sonbahar, Kış)", source: "Feature Engineering" },
        { name: "UrunKategorisi", description: "Ürün kategorisi", source: "NLP ile oluşturulan" },
        { name: "MusteriSegmenti", description: "Müşteri segmenti (RFM analizi)", source: "Analitik" },
        { name: "SubeButcesi", description: "Şubenin aylık satış bütçesi", source: "Dış veri" }
      ]
    },
    external: {
      description: "Model doğruluğunu artırmak için dış veri kaynaklarından özellikler ekledik",
      datasets: [
        {
          name: "Türkiye Resmi Tatil Günleri",
          source: "T.C. Resmi Gazete",
          link: "https://www.resmigazete.gov.tr/",
          features: ["TatilGunu", "TatilAdi", "TatilTipi"]
        },
        {
          name: "Türkiye İl Bazlı Hava Durumu",
          source: "Meteoroloji Genel Müdürlüğü",
          link: "https://www.mgm.gov.tr/",
          features: ["Sicaklik", "Yagis", "HavaDurumu"]
        },
        {
          name: "Türkiye Ekonomik Göstergeler",
          source: "TÜİK",
          link: "https://www.tuik.gov.tr/",
          features: ["TuketiciGuvenEndeksi", "PerakendeSatisEndeksi", "EnflasyonOrani"]
        }
      ]
    }
  },

  // Özet metrikler ve doğruluk değerlerini rasyonel hale getirdik
  metrics: {
    accuracy: {
      mae: 12.4,
      mape: 6.8,
      rmse: 18.2,
      r2: 0.936
    },

    // Algoritma karşılaştırmaları
    algorithms: [
      {
        name: "XGBoost",
        mae: 12.4,
        mape: 6.8,
        rmse: 18.2,
        r2: 0.936,
        trainingTime: "08:45",
        forecasts: {
          daily: { accuracy: 93.6, confidence: 90.2 },
          weekly: { accuracy: 94.8, confidence: 91.5 },
          monthly: { accuracy: 95.7, confidence: 92.8 }
        }
      },
      {
        name: "LightGBM",
        mae: 13.8,
        mape: 7.2,
        rmse: 19.6,
        r2: 0.924,
        trainingTime: "07:12",
        forecasts: {
          daily: { accuracy: 92.4, confidence: 89.1 },
          weekly: { accuracy: 93.5, confidence: 90.3 },
          monthly: { accuracy: 94.2, confidence: 91.4 }
        }
      },
      { name: "Random Forest", mae: 15.2, mape: 7.8, rmse: 21.4, r2: 0.912, trainingTime: "12:30" },
      { name: "Prophet", mae: 16.7, mape: 8.4, rmse: 23.8, r2: 0.894, trainingTime: "05:20" },
      { name: "ARIMA", mae: 18.9, mape: 9.2, rmse: 25.6, r2: 0.878, trainingTime: "04:15" }
    ],

    // İş değeri metriklerini daha rasyonel yapıyoruz ve parasal değerler TL ile gösteriyoruz
    businessValue: {
      stockReduction: "₺842.000",
      revenueIncrease: "₺1.250.000",
      costSavings: "₺465.000",
      outOfStockReduction: "42%",
      forecastingTime: "28 saat/ay"
    }
  },

  // Veri görselleştirme - Türkiye'ye özel satış verisi (TL)
  visualizations: {
    // Aylık satış trendleri - 2024 (hem gerçek hem tahmin değerleri gösterilecek)
    salesTrends: [
      { month: "Oca 2024", sales: 32500, forecast: 31800 },
      { month: "Şub 2024", sales: 31200, forecast: 30800 },
      { month: "Mar 2024", sales: 35800, forecast: 35200 },
      { month: "Nis 2024", sales: 34500, forecast: 34000 },
      { month: "May 2024", sales: 36800, forecast: 36200 },
      { month: "Haz 2024", sales: 39200, forecast: 38700 },
      { month: "Tem 2024", sales: 42400, forecast: 41500 },
      { month: "Ağu 2024", sales: 45800, forecast: 44800 },
      { month: "Eyl 2024", sales: 39500, forecast: 39200 },
      { month: "Eki 2024", sales: 41500, forecast: 40500 },
      { month: "Kas 2024", sales: 48500, forecast: 47500 },
      { month: "Ara 2024", sales: 53200, forecast: 52500 }
    ],

    // Şehir bazlı satışlar - Türkiye şehirleri
    citySales: [
      { city: "İstanbul", sales: 12450000 },
      { city: "Ankara", sales: 5850000 },
      { city: "İzmir", sales: 4250000 },
      { city: "Antalya", sales: 3150000 },
      { city: "Bursa", sales: 2950000 },
      { city: "Adana", sales: 1850000 },
      { city: "Diğer", sales: 4850000 }
    ],

    // Kategori dağılımı
    categoryDistribution: [
      { category: "Gıda", value: 42 },
      { category: "Temizlik", value: 20 },
      { category: "İçecek", value: 15 },
      { category: "Kişisel Bakım", value: 12 },
      { category: "Ev Gereçleri", value: 7 },
      { category: "Diğer", value: 4 }
    ],

    // Günlük satış modeli
    weekdayPattern: [
      { day: "Pazartesi", sales: 4250000, orders: 32560 },
      { day: "Salı", sales: 4120000, orders: 31240 },
      { day: "Çarşamba", sales: 4380000, orders: 33750 },
      { day: "Perşembe", sales: 4650000, orders: 35420 },
      { day: "Cuma", sales: 6250000, orders: 42360 },
      { day: "Cumartesi", sales: 7850000, orders: 48520 },
      { day: "Pazar", sales: 3950000, orders: 28450 }
    ],

    // Saatlik satış deseni
    hourlyPattern: [
      { hour: "09-11", sales: 3250000, transactions: 24580 },
      { hour: "11-13", sales: 5150000, transactions: 38920 },
      { hour: "13-15", sales: 4850000, transactions: 36750 },
      { hour: "15-17", sales: 4350000, transactions: 33240 },
      { hour: "17-19", sales: 6280000, transactions: 47620 },
      { hour: "19-21", sales: 5950000, transactions: 45130 }
    ]
  },

  // Özellik önem düzeyleri - Türkçe olarak güncellendi
  featureImportance: [
    { feature: "Geçmiş Hafta Satışları", importance: 100 },
    { feature: "Önceki Gün Satışları", importance: 85 },
    { feature: "Ay", importance: 72 },
    { feature: "Haftanın Günü", importance: 65 },
    { feature: "Fiyat Değişimi", importance: 58 },
    { feature: "Tatil Günü", importance: 52 },
    { feature: "Promosyon Aktif", importance: 48 },
    { feature: "Hava Durumu", importance: 40 },
    { feature: "Rakip Fiyatları", importance: 35 },
    { feature: "Ürün Kategorisi", importance: 30 }
  ],

  // Örnek tahmin sonuçları - TL para birimi için güncellendi
  forecastResults: [
    { date: "01.01.2025", actual: 1850, forecast: 1824, lower: 1750, upper: 1890 },
    { date: "02.01.2025", actual: 1920, forecast: 1896, lower: 1820, upper: 1970 },
    { date: "03.01.2025", actual: 1980, forecast: 1948, lower: 1870, upper: 2020 },
    { date: "04.01.2025", actual: 1850, forecast: 1820, lower: 1750, upper: 1890 },
    { date: "05.01.2025", actual: 1780, forecast: 1750, lower: 1680, upper: 1820 },
    { date: "06.01.2025", actual: 2120, forecast: 2080, lower: 2000, upper: 2160 },
    { date: "07.01.2025", actual: 2380, forecast: 2340, lower: 2250, upper: 2430 },
    { date: "08.01.2025", actual: 1740, forecast: 1710, lower: 1640, upper: 1780 },
    { date: "09.01.2025", actual: 1880, forecast: 1850, lower: 1780, upper: 1920 },
    { date: "10.01.2025", actual: 1950, forecast: 1920, lower: 1850, upper: 1990 }
  ],

  // Ürün bazlı tahmin doğruluğu - TL para birimi ile güncellendi
  productAccuracy: [
    { product: "Süt", mape: 4.8, sales: 8250000 },
    { product: "Ekmek", mape: 6.5, sales: 7450000 },
    { product: "Su", mape: 5.2, sales: 6850000 },
    { product: "Yumurta", mape: 7.8, sales: 4250000 },
    { product: "Pirinç", mape: 8.6, sales: 3850000 },
    { product: "Çay", mape: 7.2, sales: 3250000 },
    { product: "Şeker", mape: 9.5, sales: 2950000 },
    { product: "Makarna", mape: 8.3, sales: 2650000 }
  ],

  // Türkiye market zinciri için daha uygun öneriler
  recommendations: [
    {
      title: "Şube Bazlı Stok Kontrolü",
      description: "Bölgesel satış dinamiklerine göre otomatik stok seviye yönetimi",
      impact: "%15 Stok Tasarrufu"
    },
    {
      title: "Kampanya Talep Tahmini",
      description: "Promosyon dönemlerindeki talebin geçmiş verilere göre analizi",
      impact: "%28 Raf Verimliliği"
    },
    {
      title: "Mevsimsel Planlama",
      description: "Mevsimlik ürün talebinin hava durumu verileriyle modellenmesi",
      impact: "%22 Daha Az Fire"
    }
  ],

  // ROI analizi yerine daha gerçekçi POC değerlendirme kriterleri
  pocEvaluation: {
    technicalFeasibility: "Yüksek",
    implementationComplexity: "Orta",
    timeRequired: "3-4 Hafta",
    potentialImpact: "Olumlu",
    nextSteps: "Genişletilmiş pilot uygulama ve veri zenginleştirme"
  },

  // Eklediğimiz özet değerlendirme bölümü
  pocSummary: {
    technicalInsights: [
      "XGBoost algoritması test edilen senaryolarda en iyi performansı gösterdi",
      "Veri kalitesi ve eksik veriler tahmin doğruluğunu etkilemektedir",
      "En az 6 aylık geçmiş veri ile makul doğruluk seviyeleri elde edilebilir",
      "Günlük seviyede %90+ doğruluk elde edilebilirken, uzun vadeli tahminlerde doğruluk azalmaktadır"
    ],
    businessLearnings: [
      "A kategori ürünlerde tahmin doğruluğu B ve C kategorilerine göre daha yüksektir",
      "Mevsimsel ürünler için özel modelleme yaklaşımları gerekmektedir",
      "Promosyon etkisi modele dahil edildiğinde tahmin doğruluğu artmaktadır",
      "Şube bazlı tahminlerde ek demografik veriler faydalı olacaktır"
    ],
    limitations: [
      "Mevcut POC sadece belirli ürün kategorileri için test edilmiştir",
      "Sınırlı veri seti nedeniyle uzun vadeli tahminlerde risk bulunmaktadır",
      "Dış faktörlerin (piyasa değişimleri, rekabet) etkisi henüz modellenmemiştir",
      "Gerçek zamanlı uygulama için altyapı gereksinimleri test edilmemiştir"
    ]
  },

  // ...existing code...
  // Adding data preparation steps property
  dataPreparationSteps: [
    "Veri seti temizliği ve aykırı değerlerin (outliers) elenmesi",
    "Zaman serisi özellikleri ve gecikmeli (lag) verilerin oluşturulması",
    "Harici verilerin (hava durumu, resmi tatiller) sisteme entegrasyonu",
    "Eğitim ve test setlerinin kronolojik olarak ayrılması"
  ],

  // Model Deployment bilgileri
  deployment: {
    steps: [
      "Model parametrelerinin optimize edilmesi",
      "Model dönüştürülerek servise hazırlanması (ONNX formatına çevirme)",
      "Azure ML Service'e deploy edilmesi",
      "REST API oluşturulması",
      "Entegrasyon testleri ve doğrulama",
      "Performans izleme ve ölçümleme sisteminin kurulması",
      "Yeniden eğitim planının oluşturulması"
    ],
    performance: {
      inferenceTime: "250ms / tahmin",
      batchSize: "10,000 ürün/dakika",
      scalability: "Otomatik ölçeklendirme aktif",
      reliability: "%99.95 uptime"
    }
  },

  // Code Snippets
  codeSnippets: {
    dataPreparation: `# Veri Hazırlama ve Temizleme
import pandas as pd
import numpy as np
from datetime import datetime, timedelta
import matplotlib.pyplot as plt
import seaborn as sns

# Veri setini yükleme
df = pd.read_csv('market_sales_data.csv', parse_dates=['SatisTarihi'])

# Eksik değerleri kontrol etme
print(f"Eksik değerler:\\n{df.isnull().sum()}")

# Eksik değerleri doldurma (sayısal kolonlar için)
df['Miktar'].fillna(df['Miktar'].median(), inplace=True)
df['BirimFiyat'].fillna(df['BirimFiyat'].median(), inplace=True)

# Aykırı değerleri tespit etme ve filtreleme (IQR yöntemi)
def remove_outliers(df, column):
    Q1 = df[column].quantile(0.25)
    Q3 = df[column].quantile(0.75)
    IQR = Q3 - Q1
    lower_bound = Q1 - 1.5 * IQR
    upper_bound = Q3 + 1.5 * IQR
    return df[(df[column] >= lower_bound) & (df[column] <= upper_bound)]

df = remove_outliers(df, 'Miktar')
df = remove_outliers(df, 'BirimFiyat')

# Tarih özelliklerini çıkarma
df['Tarih'] = df['SatisTarihi'].dt.date
df['Yil'] = df['SatisTarihi'].dt.year
df['Ay'] = df['SatisTarihi'].dt.month
df['Gun'] = df['SatisTarihi'].dt.day
df['HaftaninGunu'] = df['SatisTarihi'].dt.dayofweek
df['HaftaSonu'] = df['HaftaninGunu'].apply(lambda x: 1 if x >= 5 else 0)
df['Mevsim'] = df['Ay'].apply(lambda x: 
                             'Kış' if x in [12, 1, 2] else
                             'İlkbahar' if x in [3, 4, 5] else
                             'Yaz' if x in [6, 7, 8] else 'Sonbahar')

# Toplam tutar hesaplama
df['ToplamTutar'] = df['Miktar'] * df['BirimFiyat']

# Kategorik değişkenleri sayısal forma dönüştürme
mevsim_map = {'Kış': 0, 'İlkbahar': 1, 'Yaz': 2, 'Sonbahar': 3}
df['MevsimKod'] = df['Mevsim'].map(mevsim_map)

# Ürün ve şube seviyesinde toplamları hesaplama
urun_ozeti = df.groupby(['UrunKodu', 'UrunAdi']).agg({
    'Miktar': 'sum',
    'ToplamTutar': 'sum'
}).reset_index().rename(columns={'Miktar': 'ToplamMiktar', 'ToplamTutar': 'ToplamSatis'})

sube_ozeti = df.groupby(['SubeKodu', 'SubeSehir']).agg({
    'Miktar': 'sum',
    'ToplamTutar': 'sum',
    'FisNo': 'nunique'
}).reset_index().rename(columns={
    'Miktar': 'ToplamMiktar', 
    'ToplamTutar': 'ToplamSatis',
    'FisNo': 'IslemSayisi'
})

# Veriyi görselleştirme
plt.figure(figsize=(14, 8))

# Aylık satış trendi
monthly_sales = df.groupby(['Yil', 'Ay'])['ToplamTutar'].sum().reset_index()
monthly_sales['Tarih'] = monthly_sales.apply(lambda x: datetime(int(x['Yil']), int(x['Ay']), 1), axis=1)
plt.subplot(2, 2, 1)
plt.plot(monthly_sales['Tarih'], monthly_sales['ToplamTutar'])
plt.title('Aylık Satış Trendi')
plt.xticks(rotation=45)

# Haftanın günlerine göre satışlar
weekday_sales = df.groupby('HaftaninGunu')['ToplamTutar'].sum().reset_index()
weekday_names = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']
weekday_sales['Gun'] = weekday_sales['HaftaninGunu'].apply(lambda x: weekday_names[x])
plt.subplot(2, 2, 2)
sns.barplot(x='Gun', y='ToplamTutar', data=weekday_sales)
plt.title('Haftanın Günlerine Göre Satışlar')
plt.xticks(rotation=45)

plt.tight_layout()
plt.show()`,

    modelTraining: `# Model Eğitimi
import pandas as pd
import numpy as np
from sklearn.model_selection import train_test_split, TimeSeriesSplit, GridSearchCV
from sklearn.preprocessing import StandardScaler
from sklearn.ensemble import RandomForestRegressor
import xgboost as xgb
from sklearn.metrics import mean_absolute_error, mean_squared_error, r2_score
import matplotlib.pyplot as plt
import seaborn as sns

# Eğitim için veri hazırlama
def prepare_time_series_data(df, target_col, lag_periods=[1, 7, 14, 28]):
    df_copy = df.copy()
    
    # Lag özellikleri oluştur (geçmiş değerleri özellik olarak ekle)
    for lag in lag_periods:
        df_copy[f'lag_{lag}'] = df_copy[target_col].shift(lag)
    
    # NaN değerleri temizle
    df_copy.dropna(inplace=True)
    
    return df_copy

# Veriyi grup bazında eğitim/test olarak böl
def train_test_split_by_time(df, test_ratio=0.2):
    split_idx = int(len(df) * (1 - test_ratio))
    train = df.iloc[:split_idx].copy()
    test = df.iloc[split_idx:].copy()
    return train, test

# Ana eğitim fonksiyonu
def train_forecasting_model(train_df, test_df, target_col, feature_cols):
    # Eğitim ve test setlerini hazırla
    X_train = train_df[feature_cols]
    y_train = train_df[target_col]
    X_test = test_df[feature_cols]
    y_test = test_df[target_col]
    
    # XGBoost modeli eğit
    model = xgb.XGBRegressor(
        n_estimators=200,
        learning_rate=0.05,
        max_depth=5,
        subsample=0.8,
        colsample_bytree=0.8,
        random_state=42
    )
    
    # Model eğitimi
    model.fit(
        X_train, y_train,
        eval_set=[(X_train, y_train), (X_test, y_test)],
        eval_metric='rmse',
        early_stopping_rounds=20,
        verbose=True
    )
    
    # Test tahminleri
    y_pred = model.predict(X_test)
    
    # Metrikler
    mae = mean_absolute_error(y_test, y_pred)
    rmse = np.sqrt(mean_squared_error(y_test, y_pred))
    r2 = r2_score(y_test, y_pred)
    
    print(f"Model Performance Metrics:")
    print(f"MAE: {mae:.2f}")
    print(f"RMSE: {rmse:.2f}")
    print(f"R²: {r2:.4f}")
    
    # Özellik önem derecelerini görselleştirme
    plt.figure(figsize=(10, 6))
    sorted_idx = np.argsort(model.feature_importances_)
    plt.barh(np.array(feature_cols)[sorted_idx], model.feature_importances_[sorted_idx])
    plt.xlabel("Özellik Önem Derecesi")
    plt.title("XGBoost Özellik Önem Dereceleri")
    plt.tight_layout()
    plt.show()
    
    return model, y_pred, mae, rmse, r2

# Ana veri hazırlama ve model eğitimi
# 1. Veriyi ürün ve şube bazında topla
daily_sales = df.groupby(['Tarih', 'UrunKodu'])['Miktar'].sum().reset_index()
daily_sales = daily_sales.sort_values(['UrunKodu', 'Tarih'])

# 2. Bir örnek ürün için model eğitimi
product_id = daily_sales['UrunKodu'].unique()[0]  # Örnek bir ürün seç
product_data = daily_sales[daily_sales['UrunKodu'] == product_id].copy()

# 3. Zaman serisi özellikleri oluştur
ts_data = prepare_time_series_data(product_data, 'Miktar')

# 4. Eğitim test bölünmesi
train_data, test_data = train_test_split_by_time(ts_data, test_ratio=0.2)

# 5. Özellik sütunları
feature_cols = [col for col in ts_data.columns if 'lag_' in col]

# 6. Model eğitimi
model, predictions, mae, rmse, r2 = train_forecasting_model(
    train_data, test_data, 'Miktar', feature_cols
)

# 7. Gerçek vs Tahmin grafikleri
plt.figure(figsize=(14, 7))
plt.plot(test_data['Tarih'], test_data['Miktar'], label='Gerçek Değer', color='blue')
plt.plot(test_data['Tarih'], predictions, label='Tahmin', color='red', linestyle='--')
plt.title(f'Ürün {product_id} için Talep Tahmini')
plt.xlabel('Tarih')
plt.ylabel('Miktar')
plt.legend()
plt.grid(True)
plt.tight_layout()
plt.show()`,

    evaluation: `# Model Değerlendirme ve Karşılaştırma
import pandas as pd
import numpy as np
from sklearn.metrics import mean_absolute_error, mean_squared_error, r2_score, mean_absolute_percentage_error
import matplotlib.pyplot as plt
import seaborn as sns
from sklearn.model_selection import TimeSeriesSplit
import xgboost as xgb
from sklearn.ensemble import RandomForestRegressor
from prophet import Prophet
import statsmodels.api as sm

# Farklı modelleri karşılaştırma fonksiyonu
def compare_models(data, target_col, feature_cols, time_col):
    # Veriyi hazırla
    X = data[feature_cols]
    y = data[target_col]
    time_series = data[time_col]
    
    # Zaman serisi çapraz doğrulama
    tscv = TimeSeriesSplit(n_splits=5)
    
    # Sonuçları saklayacak veri çerçevesi
    results = pd.DataFrame(columns=[
        'Model', 'MAE', 'MAPE', 'RMSE', 'R2', 'TrainingTime'
    ])
    
    # XGBoost
    import time
    start_time = time.time()
    
    xgb_model = xgb.XGBRegressor(
        n_estimators=200,
        learning_rate=0.05,
        max_depth=5,
        subsample=0.8,
        colsample_bytree=0.8,
        random_state=42
    )
    
    xgb_scores = []
    
    for train_idx, test_idx in tscv.split(X):
        X_train, X_test = X.iloc[train_idx], X.iloc[test_idx]
        y_train, y_test = y.iloc[train_idx], y.iloc[test_idx]
        
        xgb_model.fit(X_train, y_train)
        xgb_preds = xgb_model.predict(X_test)
        
        xgb_scores.append({
            'MAE': mean_absolute_error(y_test, xgb_preds),
            'MAPE': mean_absolute_percentage_error(y_test, xgb_preds),
            'RMSE': np.sqrt(mean_squared_error(y_test, xgb_preds)),
            'R2': r2_score(y_test, xgb_preds)
        })
    
    xgb_time = time.time() - start_time
    
    # Ortalama sonuçları hesapla
    xgb_mean_scores = {metric: np.mean([score[metric] for score in xgb_scores]) for metric in xgb_scores[0].keys()}
    
    # Sonuçları kaydet
    results = results.append({
        'Model': 'XGBoost',
        'MAE': xgb_mean_scores['MAE'],
        'MAPE': xgb_mean_scores['MAPE'],
        'RMSE': xgb_mean_scores['RMSE'],
        'R2': xgb_mean_scores['R2'],
        'TrainingTime': f"{xgb_time:.2f}s"
    }, ignore_index=True)
    
    // Random Forest
    start_time = time.time();
    
    rf_model = RandomForestRegressor(
        n_estimators=200,
        max_depth=10,
        random_state=42,
        n_jobs=-1
    );
    
    rf_scores = [];
    
    for (train_idx, test_idx) in tscv.split(X):
        X_train = X.iloc[train_idx];
        X_test = X.iloc[test_idx];
        y_train = y.iloc[train_idx];
        y_test = y.iloc[test_idx];
        
        rf_model.fit(X_train, y_train);
        rf_preds = rf_model.predict(X_test);
        
        rf_scores.append({
            'MAE': mean_absolute_error(y_test, rf_preds),
            'MAPE': mean_absolute_percentage_error(y_test, rf_preds),
            'RMSE': np.sqrt(mean_squared_error(y_test, rf_preds)),
            'R2': r2_score(y_test, rf_preds)
        });
    
    rf_time = time.time() - start_time;
    
    // Ortalama sonuçları hesapla
    rf_mean_scores = {metric: np.mean([score[metric] for score in rf_scores]) for metric in rf_scores[0].keys()};
    
    // Sonuçları kaydet
    results = results.append({
        'Model': 'Random Forest',
        'MAE': rf_mean_scores['MAE'],
        'MAPE': rf_mean_scores['MAPE'],
        'RMSE': rf_mean_scores['RMSE'],
        'R2': rf_mean_scores['R2'],
        'TrainingTime': f"{rf_time:.2f}s"
    }, ignore_index=True);
    
    // ARIMA (örnek bir zaman serisi)
    // Not: ARIMA daha karmaşık bir yapıya sahiptir, burada basitleştirilmiştir
    start_time = time.time();
    
    arima_scores = [];
    
    try:
        for (train_idx, test_idx) in list(tscv.split(X))[:1]:  // İlk split'i kullan
            y_train = y.iloc[train_idx];
            y_test = y.iloc[test_idx];
            
            // ARIMA modeli
            model = sm.tsa.ARIMA(y_train, order=(5,1,0));
            model_fit = model.fit();
            
            // Tahmin
            arima_preds = model_fit.forecast(steps=len(y_test));
            
            arima_scores.append({
                'MAE': mean_absolute_error(y_test, arima_preds),
                'MAPE': mean_absolute_percentage_error(y_test, arima_preds),
                'RMSE': np.sqrt(mean_squared_error(y_test, arima_preds)),
                'R2': r2_score(y_test, arima_preds)
            });
        
        arima_time = time.time() - start_time;
        
        // Ortalama sonuçları hesapla
        arima_mean_scores = {metric: np.mean([score[metric] for score in arima_scores]) for metric in arima_scores[0].keys()};
        
        // Sonuçları kaydet
        results = results.append({
            'Model': 'ARIMA',
            'MAE': arima_mean_scores['MAE'],
            'MAPE': arima_mean_scores['MAPE'],
            'RMSE': arima_mean_scores['RMSE'],
            'R2': arima_mean_scores['R2'],
            'TrainingTime': f"{arima_time:.2f}s"
        }, ignore_index=True);
    } catch {
        print("ARIMA modeli bazı seriler için uygun olmayabilir.");
    }
    
    // Sonuçları görselleştir
    plt.figure(figsize=(12, 10));
    
    // MAE
    plt.subplot(2, 2, 1);
    sns.barplot(x='Model', y='MAE', data=results);
    plt.title('Mean Absolute Error');
    
    // MAPE
    plt.subplot(2, 2, 2);
    sns.barplot(x='Model', y='MAPE', data=results);
    plt.title('Mean Absolute Percentage Error');
    
    // RMSE
    plt.subplot(2, 2, 3);
    sns.barplot(x='Model', y='RMSE', data=results);
    plt.title('Root Mean Squared Error');
    
    // R2
    plt.subplot(2, 2, 4);
    sns.barplot(x='Model', y='R2', data=results);
    plt.title('R² Score');
    
    plt.tight_layout();
    plt.show();
    
    return results;
}

// Ana değerlendirme çağrısı
model_comparison_results = compare_models(
    ts_data, 
    'Miktar', 
    feature_cols, 
    'Tarih'
);

print("Model Karşılaştırma Sonuçları:");
print(model_comparison_results);

// Gerçek vs Tahmin karşılaştırma grafiği
plt.figure(figsize=(14, 7));

// Tüm tahmin edilen değerler için tek grafik
plt.plot(test_data['Tarih'], test_data['Miktar'], 'b-', label='Gerçek Değerler');
plt.plot(test_data['Tarih'], predictions, 'r--', label='XGBoost Tahminleri');

plt.title('Gerçek vs Tahmin Karşılaştırması');
plt.xlabel('Tarih');
plt.ylabel('Miktar');
plt.legend();
plt.grid(True);

// Güven aralığı da ekle
confidence = 0.2;  // %20 güven aralığı
plt.fill_between(
    test_data['Tarih'],
    predictions * (1 - confidence),
    predictions * (1 + confidence),
    color='red',
    alpha=0.1,
    label='%80 Güven Aralığı'
);

plt.tight_layout();
plt.show();`,

    deployment: `# Model Deployment ve Entegrasyon

# 1. Modeli kaydet ve dönüştür
import pickle
import joblib
import time
import pandas as pd
import numpy as np
import os
import json
from datetime import datetime, timedelta
import xgboost as xgb
from flask import Flask, request, jsonify

# Eğitilmiş modeli kaydet
def save_model(model, model_path='models/xgboost_demand_forecast_model.pkl'):
    // Dizini oluştur (eğer yoksa)
    os.makedirs(os.path.dirname(model_path), exist_ok=True);
    
    // Modeli kaydet
    joblib.dump(model, model_path);
    print(f"Model kaydedildi: {model_path}");
    
    return model_path;
}

// Model meta bilgilerini kaydet
def save_model_metadata(model_info, metadata_path='models/model_metadata.json'):
    metadata = {
        'model_version': model_info['version'],
        'created_at': datetime.now().isoformat(),
        'features': model_info['features'],
        'target': model_info['target'],
        'performance': {
            'mae': model_info['metrics']['mae'],
            'rmse': model_info['metrics']['rmse'],
            'r2': model_info['metrics']['r2']
        },
        'training_data_range': {
            'start_date': model_info['training_range']['start'],
            'end_date': model_info['training_range']['end']
        }
    };
    
    // Dizini oluştur (eğer yoksa)
    os.makedirs(os.path.dirname(metadata_path), exist_ok=True);
    
    // Meta verileri kaydet
    with open(metadata_path, 'w') as f:
        json.dump(metadata, f, indent=4);
    
    print(f"Model meta verileri kaydedildi: {metadata_path}");
    
    return metadata_path;
}

// 2. API Oluşturma (Flask Örneği)
app = Flask(__name__);

// Model yükleyici
def load_model(model_path='models/xgboost_demand_forecast_model.pkl'):
    return joblib.load(model_path);
}

// Model versiyonu ve meta verilerini yükleyici
def load_model_metadata(metadata_path='models/model_metadata.json'):
    with open(metadata_path, 'r') as f:
        return json.load(f);
}

// Global değişkenler
MODEL = None;
MODEL_METADATA = None;

// API'yi başlat
def initialize_api():
    global MODEL, MODEL_METADATA;
    MODEL = load_model();
    MODEL_METADATA = load_model_metadata();
    print("API başlatıldı ve model yüklendi.");
}

// Tahmin endpointi
@app.route('/predict', methods=['POST'])
def predict():
    try:
        // İstek verilerini al
        data = request.json;
        
        // Gerekli girdileri kontrol et
        required_features = MODEL_METADATA['features'];
        for (feature) in required_features:
            if (feature not in data):
                return jsonify({
                    'error': f'Eksik özellik: {feature}',
                    'required_features': required_features
                }), 400;
        
        // Verileri DataFrame'e dönüştür
        input_data = pd.DataFrame([data]);
        
        // Tahmini yap
        start_time = time.time();
        prediction = MODEL.predict(input_data[required_features])[0];
        inference_time = time.time() - start_time;
        
        // Sonucu döndür
        return jsonify({
            'prediction': float(prediction),
            'inference_time_ms': round(inference_time * 1000, 2),
            'model_version': MODEL_METADATA['model_version'],
            'timestamp': datetime.now().isoformat()
        });
    
    } catch (Exception as e) {
        return jsonify({
            'error': str(e)
        }), 500;
}

// Bulk tahmin endpointi
@app.route('/predict/batch', methods=['POST'])
def predict_batch():
    try:
        // İstek verilerini al
        data = request.json;
        
        if ('items' not in data or not isinstance(data['items'], list)):
            return jsonify({
                'error': 'Geçersiz format. "items" listesi gerekli.'
            }), 400;
        
        // Gerekli girdileri kontrol et
        required_features = MODEL_METADATA['features'];
        
        // Verileri DataFrame'e dönüştür
        input_data = pd.DataFrame(data['items']);
        
        // Eksik özellikleri kontrol et
        missing_features = [f for f in required_features if f not in input_data.columns];
        if (missing_features):
            return jsonify({
                'error': f'Eksik özellikler: {missing_features}',
                'required_features': required_features
            }), 400;
        
        // Tahmini yap
        start_time = time.time();
        predictions = MODEL.predict(input_data[required_features]).tolist();
        inference_time = time.time() - start_time;
        
        // Sonuçları döndür
        return jsonify({
            'predictions': predictions,
            'count': len(predictions),
            'inference_time_ms': round(inference_time * 1000, 2),
            'model_version': MODEL_METADATA['model_version'],
            'timestamp': datetime.now().isoformat()
        });
    
    } catch (Exception as e) {
        return jsonify({
            'error': str(e)
        }), 500;
}

// Model bilgisi endpointi
@app.route('/model/info', methods=['GET'])
def model_info():
    return jsonify(MODEL_METADATA);
}

// API'yi başlat
if __name__ == '__main__':
    initialize_api();
    app.run(host='0.0.0.0', port=5000, debug=True);

// 3. Azure ML Service Deployment
// Not: Bu kısım için Azure ML SDK kullanılır (gerçek uygulamada)
/*
from azureml.core import Workspace, Model, Environment
from azureml.core.webservice import AciWebservice, Webservice
from azureml.core.model import InferenceConfig

// Workspace'e bağlan
ws = Workspace.from_config();

// Ortamı tanımla
env = Environment.from_conda_specification(name="demand_forecast_env", file_path="conda_dependencies.yml");

// Çıkarım konfigürasyonu oluştur
inference_config = InferenceConfig(entry_script="score.py", environment=env);

// Deployment konfigürasyonu
deployment_config = AciWebservice.deploy_configuration(
    cpu_cores=1,
    memory_gb=1,
    tags={"data": "demand_forecast"},
    description="Talep tahmin hizmeti"
);

// Modeli deploy et
service = Model.deploy(
    workspace=ws,
    name="demand-forecast-service",
    models=[model],
    inference_config=inference_config,
    deployment_config=deployment_config
);

service.wait_for_deployment(show_output=True);
*/

// 4. CI/CD ve Monitoring (Örnek Açıklama)
/*
// CI/CD Pipeline (Azure DevOps veya GitHub Actions)
1. Veri doğrulama testleri
2. Model eğitimi
3. Model performans değerlendirmesi
4. Model kayıt
5. Test ortamına deployment
6. Entegrasyon testleri
7. Üretim ortamına deployment

// Model İzleme ve Yeniden Eğitim
1. Model performans metriklerini düzenli kontrol et
2. Concept drift tespiti
3. Belirli aralıklarla veya performans düşüşünde otomatik yeniden eğitim
4. A/B testleri ile kademeli geçiş
*/

print("Deployment ve entegrasyon işlemleri tamamlandı.");
    `
  }
};

// First, update the periodOptions with more detailed information
const periodOptions = [
  {
    value: 'daily',
    label: 'Günlük Tahmin',
    description: '14 günlük detaylı projeksiyon',
    multiplier: 1,
    confidenceInterval: 0.1
  },
  {
    value: 'weekly',
    label: 'Haftalık Tahmin',
    description: '8 haftalık orta vadeli öngörü',
    multiplier: 7,
    confidenceInterval: 0.15
  },
  {
    value: 'monthly',
    label: 'Aylık Tahmin',
    description: '6 aylık uzun vadeli plan',
    multiplier: 30,
    confidenceInterval: 0.2
  }
];

// TalepTahminiPOC bileşenini güncelliyoruz
const TalepTahminiPOC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('XGBoost');


  // Add new state variables
  const [selectedPeriod, setSelectedPeriod] = useState('daily');


  // Bölüm referansları
  const datasetRef = useRef(null);
  const dataPreparationRef = useRef(null);
  const visualizationRef = useRef(null);
  const featureImportanceRef = useRef(null);
  const forecastResultsRef = useRef(null);
  const recommendationsRef = useRef(null);

  const data = retailDemandForecastingPOC;

  // Add new state for animated sections visibility
  const [animatedSections, setAnimatedSections] = useState({
    main: false,
    dataset: false,
    metrics: false,
    preparation: false,
    features: false,
    forecast: false
  });

  // Sayfa yüklendiğinde loading durumunu simüle etme
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);

      // Begin animations after loading completes
      setTimeout(() => setAnimatedSections(prev => ({ ...prev, main: true })), 300);
      setTimeout(() => setAnimatedSections(prev => ({ ...prev, dataset: true })), 600);
      setTimeout(() => setAnimatedSections(prev => ({ ...prev, metrics: true })), 900);
      setTimeout(() => setAnimatedSections(prev => ({ ...prev, preparation: true })), 1200);
      setTimeout(() => setAnimatedSections(prev => ({ ...prev, features: true })), 1500);
      setTimeout(() => setAnimatedSections(prev => ({ ...prev, forecast: true })), 1800);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // İçindekiler kısmından ilgili bölüme scroll yapma
  const scrollToSection = (ref) => {
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };



  const handleAlgorithmChange = (event) => {
    setSelectedAlgorithm(event.target.value);
  };

  // Veri detayları için yeni state'ler ekliyoruz
  const [activeDataTab, setActiveDataTab] = useState('original');
  const [showDataDetails, setShowDataDetails] = useState(true);

  // Veri seti detaylarını gösterme/gizleme
  const toggleDataDetails = () => {
    setShowDataDetails(!showDataDetails);
  };

  // Veri tipi tab değişimi
  const handleDataTabChange = (event, newValue) => {
    setActiveDataTab(newValue);
  };

  // Veri seti açıklaması için bileşen
  const renderDatasetDescription = () => {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DatasetIcon sx={{ color: 'primary.main', mr: 1.5, fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Veri Seti Bilgileri & Zenginleştirmeler
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={showDataDetails ? <InfoIcon /> : <InfoIcon />}
            onClick={toggleDataDetails}
            sx={{ minWidth: 120 }}
          >
            {showDataDetails ? 'Özeti Göster' : 'Detayları Göster'}
          </Button>
        </Box>

        {showDataDetails ? (
          <Box>
            <Tabs
              value={activeDataTab}
              onChange={handleDataTabChange}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                value="original"
                label="Orijinal Veri Seti"
                icon={<StorageIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                value="enriched"
                label="Zenginleştirmeler"
                icon={<EnhancedEncryptionIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                value="external"
                label="Harici Kaynaklar"
                icon={<CloudDownloadIcon fontSize="small" />}
                iconPosition="start"
              />
            </Tabs>

            {activeDataTab === 'original' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {data.dataDetails.original.name}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<LinkIcon />}
                    component="a"
                    href={data.dataDetails.original.link}
                    target="_blank"
                    sx={{ ml: 2 }}
                  >
                    Kaynak
                  </Button>
                </Box>

                <Typography variant="body2" paragraph>
                  {data.dataDetails.original.description}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.lighter' }}>
                      <Typography variant="body2" color="text.secondary">Satır Sayısı</Typography>
                      <Typography variant="h6" fontWeight="bold">{data.dataDetails.original.statistics.rows}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.lighter' }}>
                      <Typography variant="body2" color="text.secondary">Kolon Sayısı</Typography>
                      <Typography variant="h6" fontWeight="bold">{data.dataDetails.original.statistics.columns}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.lighter' }}>
                      <Typography variant="body2" color="text.secondary">Ürün Sayısı</Typography>
                      <Typography variant="h6" fontWeight="bold">{data.dataDetails.original.statistics.uniqueProducts}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.lighter' }}>
                      <Typography variant="body2" color="text.secondary">Müşteri Sayısı</Typography>
                      <Typography variant="h6" fontWeight="bold">{data.dataDetails.original.statistics.uniqueCustomers}</Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.lighter' }}>
                      <Typography variant="body2" color="text.secondary">Zaman Aralığı</Typography>
                      <Typography variant="subtitle2" fontWeight="bold">1 Yıl</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Veri Özellikleri
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><Typography variant="subtitle2">Özellik</Typography></TableCell>
                        <TableCell><Typography variant="subtitle2">Açıklama</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.dataDetails.original.features.map((feature, index) => (
                        <TableRow key={index} sx={{ '&:nth-of-type(even)': { bgcolor: 'rgba(0, 0, 0, 0.02)' } }}>
                          <TableCell sx={{ fontWeight: 'medium', width: '30%' }}>{feature.name}</TableCell>
                          <TableCell>{feature.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {activeDataTab === 'enriched' && (
              <Box>
                <Typography variant="body2" paragraph>
                  {data.dataDetails.enriched.description}
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'success.lighter' }}>
                        <TableCell><Typography variant="subtitle2">Eklenen Özellik</Typography></TableCell>
                        <TableCell><Typography variant="subtitle2">Açıklama</Typography></TableCell>
                        <TableCell><Typography variant="subtitle2">Kaynak</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.dataDetails.enriched.features.map((feature, index) => (
                        <TableRow key={index} sx={{ '&:nth-of-type(even)': { bgcolor: 'rgba(0, 0, 0, 0.02)' } }}>
                          <TableCell sx={{ fontWeight: 'medium', width: '25%' }}>{feature.name}</TableCell>
                          <TableCell sx={{ width: '50%' }}>{feature.description}</TableCell>
                          <TableCell>
                            <Chip
                              label={feature.source}
                              size="small"
                              color={
                                feature.source === 'Feature Engineering' ? 'success' :
                                  feature.source === 'Hesaplanan' ? 'primary' :
                                    feature.source === 'NLP ile oluşturulan' ? 'secondary' :
                                      'default'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {activeDataTab === 'external' && (
              <Box>
                <Typography variant="body2" paragraph>
                  {data.dataDetails.external.description}
                </Typography>

                {data.dataDetails.external.datasets.map((dataset, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderLeft: '4px solid',
                      borderColor:
                        index === 0 ? 'info.main' :
                          index === 1 ? 'success.main' :
                            'warning.main'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {dataset.name}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<LinkIcon />}
                        component="a"
                        href={dataset.link}
                        target="_blank"
                      >
                        Kaynak
                      </Button>
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Sağlayıcı: {dataset.source}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {dataset.features.map((feature, i) => (
                        <Chip
                          key={i}
                          label={feature}
                          size="small"
                          icon={<AddCircleOutlineIcon fontSize="small" />}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '4px solid',
                    borderColor: 'primary.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <StorageIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Orijinal Veri Seti
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {data.dataset.name} - {data.dataset.source}
                  </Typography>
                  <Typography variant="body2">
                    {data.dataset.size} satır, {data.dataDetails.original.statistics.countries} ülke
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    onClick={() => {
                      setShowDataDetails(true);
                      setActiveDataTab('original');
                    }}
                    sx={{ alignSelf: 'flex-end', mt: 1 }}
                  >
                    Detaylar
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '4px solid',
                    borderColor: 'success.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EnhancedEncryptionIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Zenginleştirmeler
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Analitik ve özellik mühendisliği ile eklenenler
                  </Typography>
                  <Typography variant="body2">
                    {data.dataDetails.enriched.features.length} yeni özellik
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    onClick={() => {
                      setShowDataDetails(true);
                      setActiveDataTab('enriched');
                    }}
                    sx={{ alignSelf: 'flex-end', mt: 1 }}
                  >
                    Detaylar
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '4px solid',
                    borderColor: 'warning.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CloudDownloadIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Harici Veri Kaynakları
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Model doğruluğunu artırmak için eklenen veriler
                  </Typography>
                  <Typography variant="body2">
                    {data.dataDetails.external.datasets.length} kaynak, {
                      data.dataDetails.external.datasets.reduce((acc, dataset) => acc + dataset.features.length, 0)
                    } özellik
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    onClick={() => {
                      setShowDataDetails(true);
                      setActiveDataTab('external');
                    }}
                    sx={{ alignSelf: 'flex-end', mt: 1 }}
                  >
                    Detaylar
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    );
  };

  // Algoritma karşılaştırma grafiği


  // Satış trendleri grafiği
  const renderSalesTrends = () => {
    // Convert visualization data to properly show units rather than currency values
    const unitFocusedData = data.visualizations.salesTrends;

    return (
      <Box sx={{ mb: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: animatedSections.main ? 1 : 0, y: animatedSections.main ? 0 : 20 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h6" gutterBottom>
            Aylık Ürün Talep Tahminleri (2024)
          </Typography>
          <Box sx={{ height: 400, mt: 3 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={unitFocusedData}
                margin={{ top: 10, right: 30, left: 10, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="month"
                  interval={0}
                  angle={-30}
                  textAnchor="end"
                  height={60}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  tickFormatter={(value) => `${(value / 1000).toFixed(1)}K`}
                  label={{ value: 'Ürün Adedi', angle: -90, position: 'insideLeft', offset: 15 }}
                />
                <RechartsTooltip
                  formatter={(value) => [
                    `${new Intl.NumberFormat('tr-TR').format(value)} Adet`,
                    'Talep Miktarı'
                  ]}
                />
                <Legend verticalAlign="top" height={36} />
                <Bar dataKey="sales" name="Gerçek Talep" fill="#8884d8" radius={[4, 4, 0, 0]} />
                <Line
                  type="monotone"
                  dataKey="forecast"
                  name="Tahmin"
                  stroke="#ff7300"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#ff7300' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={{ display: 'flex', mt: 3 }}>
            <Box sx={{ flex: 1, pr: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Talep Gözlemleri
              </Typography>
              <Typography variant="body2" paragraph>
                Ürün talebinde Kasım ve Aralık aylarında belirgin bir artış görülmektedir. Bu artış, yılbaşı sezonu ve özel gün hazırlıklarından kaynaklanmaktadır.
              </Typography>
              <Typography variant="body2">
                Yaz ayları (Haziran, Temmuz, Ağustos) mevsimsel olarak talep yoğunluğunun arttığı bir dönem olarak gözlemlenmiştir. Bu dönemde stok seviyelerinin düzenlenmesi kritik önem taşımaktadır.
              </Typography>
            </Box>

            <Box sx={{ flex: 1, pl: 2, borderLeft: '1px solid #eee' }}>
              <Typography variant="subtitle2" gutterBottom>
                Talep Tahmin Değerlendirmesi
              </Typography>
              <Typography variant="body2" paragraph>
                XGBoost modeli, ürün talebindeki mevsimsel değişiklikleri başarıyla yakalamaktadır. Özellikle yılın son çeyreğindeki talep artışları doğru tahmin edilmiştir.
              </Typography>
              <Typography variant="body2">
                Ortalama tahmin sapması %6.3 seviyesindedir. Bu doğrulukla stok optimizasyonu sağlanarak hem raf boşluğu hem de stok maliyetleri dengeli şekilde yönetilebilir.
              </Typography>
            </Box>
          </Box>
        </motion.div>
      </Box>
    );
  };



  // Özellik önem skorları grafiği
  const renderFeatureImportance = () => {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Özellik Önem Skorları
        </Typography>
        <Box sx={{ height: 400, mt: 3 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data.featureImportance}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 150, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} />
              <YAxis type="category" dataKey="feature" width={140} />
              <RechartsTooltip formatter={(value) => [`${value}%`, 'Önem']} />
              <Legend />
              <Bar dataKey="importance" fill="#8884d8" name="Önem Yüzdesi">
                {data.featureImportance.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.importance > 70 ? '#8884d8' : entry.importance > 50 ? '#82ca9d' : '#ffc658'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Özellik Analizleri
          </Typography>
          <Typography variant="body2" paragraph>
            Görüldüğü üzere, bir önceki haftanın satışları (%100), ürünün bir önceki günkü satışları (%85), ay bilgisi (%72) ve haftanın günü (%65) en önemli özellikler olarak öne çıkmaktadır.
          </Typography>
          <Typography variant="body2" paragraph>
            Bu sonuçlar, yakın geçmiş verilerin ve mevsimsel/zamansal faktörlerin satış tahminlerinde ne kadar kritik olduğunu göstermektedir. XGBoost algoritması, bu özellikleri başarıyla yakalayarak daha doğru tahminler yapabilmektedir.
          </Typography>
        </Box>
      </Box>
    );
  };

  // Add generateForecastData function
  const generateForecastData = (period) => {
    const baseData = data.forecastResults;
    const option = periodOptions.find(p => p.value === period);
    const multiplier = option.multiplier;
    const confidence = option.confidenceInterval;

    const metrics = getSelectedAlgorithmMetrics();
    const accuracy = metrics.currentAccuracy || 95;
    const mape = 100 - accuracy;

    // Create forecast data with multiplier for different time periods (units sold, not price)
    return baseData.map((item, index) => {
      const baseForecast = Math.round(item.forecast * multiplier); // Round to whole units
      const isProjection = index > 6; // Last items will be projections

      return {
        date: item.date,
        forecast: baseForecast,
        actual: isProjection ? null : Math.round(item.actual * multiplier), // Round to whole units
        lower: Math.round(baseForecast * (1 - confidence - (mape / 100))),
        upper: Math.round(baseForecast * (1 + confidence + (mape / 100))),
        isProjection: isProjection
      };
    });
  };

  // Completely revamp the renderForecastResults function
  const renderForecastResults = () => {
    const forecastData = generateForecastData(selectedPeriod);
    const currentPeriodOption = periodOptions.find(p => p.value === selectedPeriod);
    const metrics = getSelectedAlgorithmMetrics();

    return (
      <Box sx={{ mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'primary.lighter', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {currentPeriodOption.label}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {currentPeriodOption.description}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: 'primary.main' }}>
                  Aralık Seçimi
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', lineHeight: 1.2 }}>
                  Tahminin hangi zaman dilimi (günlük, haftalık vb.) için yapılacağını seçin.
                </Typography>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white', borderRadius: 1 }}>
                  <InputLabel>Tahmin Periyodu</InputLabel>
                  <Select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    label="Tahmin Periyodu"
                  >
                    {periodOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: 'primary.main' }}>
                  Algoritma Kıyaslama
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', lineHeight: 1.2 }}>
                  Tahmin yapan farklı yapay zeka yöntemlerini seçerek sonuçları karşılaştırın.
                </Typography>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white', borderRadius: 1 }}>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={selectedAlgorithm}
                    onChange={handleAlgorithmChange}
                    label="Model"
                  >
                    {data.metrics.algorithms
                      .filter(algo => algo.forecasts)
                      .map(algo => (
                        <MenuItem key={algo.name} value={algo.name}>
                          {algo.name}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'primary.light', height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Tahmin Başarısı
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', lineHeight: 1.2 }}>
                  Seçilen yöntemin geçmiş verilere göre ne kadar doğru tahmin yaptığını gösterir.
                </Typography>
                <Typography variant="h4" color="primary" gutterBottom fontWeight="bold">
                  {metrics.currentAccuracy?.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Hata Payı: {(100 - metrics.currentAccuracy)?.toFixed(1)}%
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={forecastData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  interval={1}
                  angle={-30}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  tickFormatter={(value) => `${(value / 1000).toFixed(1)}K`}
                  domain={['auto', 'auto']}
                  label={{ value: 'Ürün Adedi', angle: -90, position: 'insideLeft', offset: 15 }}
                />
                <RechartsTooltip
                  formatter={(value, name) => {
                    if (value === null || value === undefined || name === 'upper' || name === 'lower') return null;
                    const formatted = new Intl.NumberFormat('tr-TR').format(value);
                    const label = name === 'actual' ? 'Gerçek Satış' : 'Tahmin';
                    return [`${formatted} Adet`, label];
                  }}
                />
                <Legend verticalAlign="top" height={36} />
                <Area
                  type="monotone"
                  dataKey="upper"
                  fill="#8884d8"
                  stroke="none"
                  fillOpacity={0.1}
                  name="Güven Aralığı"
                  legendType="none"
                />
                <Area
                  type="monotone"
                  dataKey="lower"
                  fill="#FFFFFF"
                  stroke="none"
                  fillOpacity={1}
                  name="Alt Sınır"
                  legendType="none"
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke="#ff7300"
                  name="Gerçek Satış"
                  strokeWidth={3}
                  dot={{ r: 5, fill: '#ff7300' }}
                  activeDot={{ r: 7 }}
                />
                <Line
                  type="monotone"
                  dataKey="forecast"
                  stroke="#8884d8"
                  name="Tahmin"
                  strokeWidth={3}
                  strokeDasharray={d => d && d.isProjection ? "5 5" : "0"}
                  dot={{ r: 4, fill: '#8884d8' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>


        </Paper>


      </Box>
    );
  };

  // İş değeri metriklerini gösterme fonksiyonu - Güncellendi, sayısal veriler kaldırıldı




  // Veri Dönüşümleri ve Hazırlama Adımları bileşeni
  const renderDataPreparationSteps = () => {
    // Check if dataPreparationSteps exists before mapping
    return (
      <Box ref={dataPreparationRef} sx={{ mb: 5, scrollMarginTop: '80px' }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Veri Dönüşümleri ve Hazırlama Adımları
        </Typography>
        {data.dataPreparationSteps ? (
          <List>
            {data.dataPreparationSteps.map((step, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <Chip label={index + 1} size="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={step} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Veri hazırlama adımları henüz tanımlanmamış.
          </Typography>
        )}
      </Box>
    );
  };

  // Add getSelectedAlgorithmMetrics function
  const getSelectedAlgorithmMetrics = () => {
    const algorithm = data.metrics.algorithms.find(a => a.name === selectedAlgorithm);
    if (!algorithm || !algorithm.forecasts || !algorithm.forecasts[selectedPeriod]) {
      return {
        currentAccuracy: 0,
        currentConfidence: 0
      };
    }
    return {
      ...algorithm,
      currentAccuracy: algorithm.forecasts[selectedPeriod].accuracy,
      currentConfidence: algorithm.forecasts[selectedPeriod].confidence
    };
  };

  // Get the consistent metrics from the default selected algorithm (e.g., XGBoost)
  const getDefaultMetrics = () => {
    // Use the base algorithm metrics for consistent display
    const defaultAlgo = data.metrics.algorithms.find(a => a.name === 'XGBoost');
    return {
      mae: defaultAlgo.mae,
      mape: defaultAlgo.mape,
      rmse: defaultAlgo.rmse,
      r2: defaultAlgo.r2
    };
  };

  // Update the section heading to be more accurate for demand forecasting
  const renderVisualizationSection = () => {
    return (
      <Box ref={visualizationRef} sx={{ mb: 5, scrollMarginTop: '80px' }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Talep Tahmin Analizi ve Görselleştirmeleri
        </Typography>
        {renderSalesTrends()}
      </Box>
    );
  };

  // Update the renderSummarySection to be more POC-focused
  const renderSummarySection = () => {
    return (
      <Box sx={{ mb: 5 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: animatedSections.forecast ? 1 : 0, y: animatedSections.forecast ? 0 : 20 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'primary.lighter' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              POC Sonuç ve Değerlendirme
            </Typography>
            <Typography variant="body1" paragraph>
              Bu POC çalışması, yapay zekanın perakende talep tahminleme süreçlerinde yüksek doğrulukla kullanılabileceğini kanıtlamıştır.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, height: '100%' }}>
                  <Typography variant="subtitle1" color="primary" gutterBottom fontWeight="bold">
                    Teknik Başarı
                  </Typography>
                  <Typography variant="body2">
                    XGBoost algoritması ile %90+ tahmin doğruluğu sağlanmış, teknik uygulanabilirlik doğrulanmıştır.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, height: '100%' }}>
                  <Typography variant="subtitle1" color="primary" gutterBottom fontWeight="bold">
                    Verimlilik Artışı
                  </Typography>
                  <Typography variant="body2">
                    Otomatik tahminleme sayesinde operasyonel planlama sürelerinde belirgin bir azalma sağlanacaktır.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, height: '100%' }}>
                  <Typography variant="subtitle1" color="primary" gutterBottom fontWeight="bold">
                    Ticari Etki
                  </Typography>
                  <Typography variant="body2">
                    Stok optimizasyonu ve raf bulunabilirliği artışıyla doğrudan ciro ve karlılık artışı hedeflenmektedir.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

          </Paper>
        </motion.div>
      </Box>
    );
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          pt: 12,
          pb: 10,
          mb: 6,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Chip
                  label="Yapay Zeka PoC Laboratuvarı"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    mb: 2,
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)'
                  }}
                />
                <Typography variant="h2" component="h1" gutterBottom fontWeight="900" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                  {data.title}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, lineHeight: 1.6, maxWidth: 700, mb: 4 }}>
                  Hangi ürünün ne kadar satılacağını önceden bilmek ister miydiniz? Bu platform, market verilerini
                  kullanarak gelecekteki talebi tahmin eder ve stoklarınızı en verimli şekilde yönetmenize
                  yardımcı olur. Yapay zekanın iş süreçlerine kattığı hızı ve doğruluğu keşfedin.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Paper sx={{ p: 1.5, px: 3, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>Doğruluk Oranı</Typography>
                    <Typography variant="h5" fontWeight="bold" color="white">93.6%</Typography>
                  </Paper>
                  <Paper sx={{ p: 1.5, px: 3, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>Veri Seti</Typography>
                    <Typography variant="h5" fontWeight="bold" color="white">320K+ Satır </Typography>
                  </Paper>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg">


        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Box sx={{ position: 'sticky', top: 20 }}>
                <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, pb: 1, borderBottom: '1px solid #eee' }}>
                    İçindekiler
                  </Typography>
                  <List sx={{ p: 0 }}>
                    <ListItem button onClick={() => scrollToSection(datasetRef)}>
                      <ListItemIcon>
                        <DataObjectIcon />
                      </ListItemIcon>
                      <ListItemText primary="Veri Seti ve Analiz Bilgileri" />
                    </ListItem>

                    <ListItem button onClick={() => scrollToSection(dataPreparationRef)}>
                      <ListItemIcon>
                        <AutoFixHighIcon />
                      </ListItemIcon>
                      <ListItemText primary="Veri Dönüşümleri ve Hazırlama" />
                    </ListItem>
                    <ListItem button onClick={() => scrollToSection(visualizationRef)}>
                      <ListItemIcon>
                        <InsightsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Talep Tahmin Analizi ve Görselleştirmeleri" />
                    </ListItem>
                    <ListItem button onClick={() => scrollToSection(featureImportanceRef)}>
                      <ListItemIcon>
                        <BiotechIcon />
                      </ListItemIcon>
                      <ListItemText primary="Özellik Önem Düzeyleri" />
                    </ListItem>
                    <ListItem button onClick={() => scrollToSection(forecastResultsRef)}>
                      <ListItemIcon>
                        <TimelineIcon />
                      </ListItemIcon>
                      <ListItemText primary="Tahmin Sonuçları" />
                    </ListItem>
                    <ListItem button onClick={() => scrollToSection(recommendationsRef)}>
                      <ListItemIcon>
                        <CheckCircleIcon />
                      </ListItemIcon>
                      <ListItemText primary="Uygulamaya Dönük Sonuçlar" />
                    </ListItem>

                  </List>

                </Paper>


              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              {/* Wrap Paper with motion.div for animation */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                      {data.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {data.description}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mt: 3 }}>

                    {/* Veri Seti Bilgileri */}
                    <Box ref={datasetRef} sx={{ mb: 5, scrollMarginTop: '80px' }}>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        Veri Seti ve Analiz Bilgileri
                      </Typography>
                      {renderDatasetDescription()}
                    </Box>

                    {/* Veri Dönüşümleri ve Hazırlama */}
                    {renderDataPreparationSteps()}

                    {/* Özellik Önem Düzeyleri */}
                    <Box ref={featureImportanceRef} sx={{ mb: 5, scrollMarginTop: '80px' }}>
                      {renderFeatureImportance()}
                    </Box>

                    {/* Tahmin Sonuçları */}
                    <Box ref={forecastResultsRef} sx={{ mb: 5, scrollMarginTop: '80px' }}>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        Tahmin Sonuçları ve Analiz
                      </Typography>
                      {renderForecastResults()}
                    </Box>




                    {/* Veri Görselleştirme ve Analiz */}
                    {renderVisualizationSection()}







                    {/* Uygulamaya Dönük Sonuçlar */}
                    <Box ref={recommendationsRef} sx={{ mb: 5, scrollMarginTop: '80px' }}>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        Uygulamaya Dönük Sonuçlar
                      </Typography>
                      <Grid container spacing={3}>
                        {data.recommendations.map((recommendation, index) => (
                          <Grid item xs={12} md={4} key={index}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                              <CardContent>
                                <Typography variant="h6" component="h3" gutterBottom>
                                  {recommendation.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                  {recommendation.description}
                                </Typography>
                                <Chip
                                  label={recommendation.impact}
                                  color="primary"
                                  size="small"
                                  sx={{ mt: 'auto' }}
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Box>

                  {/* Add the new summary section at the end */}
                  {renderSummarySection()}
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        )}
      </Container>

    </Box>
  );
};

export default TalepTahminiPOC;
